import { Component, EventEmitter, HostBinding, Input, Output } from '@angular/core';

/**
 * BasicToggleComponent
 *
 * Purpose
 * - PCC-styled toggle switch (on/off) component
 * - Provides all interaction states (unchecked, hover, focus, checked, disabled)
 * - Accessible with keyboard (Space/Enter) and ARIA attributes
 * - Supports three sizes for different UI contexts
 *
 * Visual states
 * - unchecked: default state with gray background
 * - hover: subtle border highlight on hover
 * - focus: blue border (#0671E0) with glow effect
 * - checked: primary purple (#623875) background with knob on the right
 * - disabled: grayed out appearance, no interaction
 *
 * Sizes
 * - large: Default size for most use cases
 * - medium: Medium size for compact layouts
 * - small: Small size for dense UIs
 *
 * Inputs
 * - label: string (optional) - Text displayed to the right of the toggle switch
 * - size: 'large' | 'medium' | 'small' (default: 'large')
 * - disabled: boolean (default: false) - Disables interaction and applies disabled styling
 * - checked: boolean (default: false) - Two-way bindable checked state (on/off)
 *
 * Outputs
 * - checkedChange: boolean - Emits the new checked value when user toggles the switch
 *
 * Usage examples
 *
 * <!-- Basic toggle with label -->
 * <app-basic-toggle label="Enable notifications" [(checked)]="isEnabled"></app-basic-toggle>
 *
 * <!-- Two-way binding -->
 * <app-basic-toggle [(checked)]="notificationsEnabled" label="Email notifications"></app-basic-toggle>
 *
 * <!-- Medium size -->
 * <app-basic-toggle size="medium" [(checked)]="value" label="Medium toggle"></app-basic-toggle>
 *
 * <!-- Small size -->
 * <app-basic-toggle size="small" [(checked)]="value" label="Small toggle"></app-basic-toggle>
 *
 * <!-- Disabled state -->
 * <app-basic-toggle [checked]="true" [disabled]="true" label="Disabled toggle"></app-basic-toggle>
 *
 * <!-- Controlled state with change handler -->
 * <app-basic-toggle
 *   [checked]="isOn"
 *   [disabled]="isDisabled"
 *   size="large"
 *   label="Controlled toggle"
 *   (checkedChange)="onToggleChange($event)">
 * </app-basic-toggle>
 */

type ToggleSize = 'large' | 'medium' | 'small';

@Component({
    selector: 'app-basic-toggle',
    templateUrl: './basic-toggle.component.html',
    styleUrls: ['./basic-toggle.component.scss'],
    standalone: false
})
export class BasicToggleComponent {
  @Input() label: string = '';
  @Input() disabled: boolean = false;
  @Input() size: ToggleSize = 'large';
  @Input() checked: boolean = false;
  @Output() checkedChange = new EventEmitter<boolean>();

  @HostBinding('class.disabled') get isDisabledHost(): boolean { return this.disabled; }

  onToggleClick(): void {
    if (this.disabled) { return; }
    this.checked = !this.checked;
    this.checkedChange.emit(this.checked);
  }

  onKeyDown(event: KeyboardEvent): void {
    if (this.disabled) { return; }
    const key = event.key;
    if (key === ' ' || key === 'Enter') {
      event.preventDefault();
      this.onToggleClick();
    }
  }
}


